import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useLazyGetLegalEntitiesQuery } from '@app/api/LegalEntities';
import { EDIT_LEGAL_ENTITY_PATH } from '@app/constants';
import {
  useNewUserAccessRights,
  useUserPrint,
  useWindowSize,
} from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import type { NLegalEntities } from '@app/types/LegalEntities';

export const useLegalEntities = () => {
  const {
    userAccessRights: { LegalEntities },
  } = useNewUserAccessRights();

  const hasUserRightForModuleView = useMemo(
    () => LegalEntities?.IsOn,
    [LegalEntities]
  );
  const hasUserRightToEditLegalEntities = useMemo(
    () => LegalEntities?.IsEdit,
    [LegalEntities]
  );

  const { activeBranchId } = useUserPrint();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const { isMobile } = useWindowSize();

  const [isLoading, setIsLoading] = useState(false);
  const [legalEntities, setLegalEntities] = useState<
    NLegalEntities.ModelLegalEntity[]
  >([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [getLegalEntities] = useLazyGetLegalEntitiesQuery();

  const getLegalEntitiesHandler = useCallback(async () => {
    setIsLoading(true);

    try {
      const result = await getLegalEntities({
        ActiveBranchId: activeBranchId,
        includeDeleted: true,
      }).unwrap();

      setLegalEntities(result);
    } catch (error) {
      showNotification(error);
    } finally {
      setIsLoading(false);
    }
  }, [activeBranchId, getLegalEntities, showNotification]);

  const handleEdit = useCallback(
    (Id: number) => {
      navigate(`${EDIT_LEGAL_ENTITY_PATH}?id=${Id}`);
    },
    [navigate]
  );

  const handleChange = useCallback((page: number, pageSize: number) => {
    setPageNumber(page);
    setRowsPerPage(pageSize);
  }, []);

  return {
    isLoading,
    legalEntities,
    hasUserRightForModuleView,
    hasUserRightToEditLegalEntities,
    handleEdit,
    isMobile,
    handleChange,
    pageNumber,
    rowsPerPage,
    getLegalEntitiesHandler,
  };
};
